import readRedesignOrganizerTagPlacement from './readRedesignOrganizerTagPlacement';
import {
    GTAG_PLACEMENT_KEY,
    VENDOR_FACEBOOK_PIXEL,
    VENDOR_GOOGLE_ADWORDS,
    VENDOR_GOOGLE_ANALYTICS,
    VENDOR_GOOGLE_TAG_MANAGER,
    buildOrganizerTagKey,
} from './organizerTagKeys';

const FACEBOOK_PIXEL_SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';
const UNIVERSAL_ANALYTICS_SCRIPT_URL = 'https://www.google-analytics.com/analytics.js';
const GTAG_LOADER_URL = 'https://www.googletagmanager.com/gtag/js?id=';
const GTM_LOADER_URL = 'https://www.googletagmanager.com/gtm.js?id=';

const GOOGLE_TAG_MANAGER_WHITELIST = ['ga', 'sp', 'gaawc', 'gaawe', 'html'];

const FACEBOOK_PIXEL_VERSION = '2.0';

const placedTags = new Set();
const analyticsTrackerNames = new Map();

const serverPlacement = readRedesignOrganizerTagPlacement();

serverPlacement.placedTagKeys.forEach(key => placedTags.add(key));
Object.entries(serverPlacement.analyticsTrackerNames)
    .forEach(([trackingId, trackerName]) => analyticsTrackerNames.set(trackingId, trackerName));

function getServerPlacedTagKeys() {
    return [...serverPlacement.placedTagKeys];
}

function getServerDeclaredEventKeySpellings() {
    return [...serverPlacement.eventKeySpellings];
}

function getServerPlacedEventKeys() {
    return [...serverPlacement.placedEventKeys];
}

function claimPlacement(key) {
    if (placedTags.has(key)) {
        return false;
    }
    placedTags.add(key);

    return true;
}

function appendAsyncScript(source) {
    const script = document.createElement('script');
    script.async = true;
    script.src = source;
    document.head.appendChild(script);
}

function ensureDataLayer() {
    if (!Array.isArray(window.dataLayer)) {
        window.dataLayer = [];
    }

    return window.dataLayer;
}

function ensureGtag() {
    ensureDataLayer();

    if (typeof window.gtag !== 'function') {
        window.gtag = function gtag() {
            // eslint-disable-next-line prefer-rest-params
            window.dataLayer.push(arguments);
        };
    }
    if (claimPlacement(GTAG_PLACEMENT_KEY)) {
        window.gtag('js', new Date());
    }

    return window.gtag;
}

function ensureFacebookPixelStub() {
    if (typeof window.fbq === 'function') {
        return window.fbq;
    }

    const fbq = function facebookPixelQueue(...args) {
        if (fbq.callMethod) {
            fbq.callMethod(...args);

            return;
        }
        fbq.queue.push(args);
    };

    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = FACEBOOK_PIXEL_VERSION;
    fbq.queue = [];

    window.fbq = fbq;
    // eslint-disable-next-line no-underscore-dangle
    if (!window._fbq) {
        // eslint-disable-next-line no-underscore-dangle
        window._fbq = fbq;
    }

    appendAsyncScript(FACEBOOK_PIXEL_SCRIPT_URL);

    return fbq;
}

function ensureUniversalAnalyticsStub() {
    if (typeof window.ga === 'function') {
        return window.ga;
    }

    const ga = function universalAnalyticsQueue(...args) {
        ga.q.push(args);
    };

    ga.q = [];
    ga.l = Number(new Date());

    window.GoogleAnalyticsObject = 'ga';
    window.ga = ga;

    appendAsyncScript(UNIVERSAL_ANALYTICS_SCRIPT_URL);

    return ga;
}

function placeFacebookPixel(pixelId) {
    if (!claimPlacement(buildOrganizerTagKey(VENDOR_FACEBOOK_PIXEL, pixelId))) {
        return;
    }

    ensureFacebookPixelStub()('init', pixelId);
}

function trackFacebookPixelEvent(pixelId, eventName, parameters) {
    if (typeof window.fbq !== 'function') {
        return;
    }
    if (parameters === null || parameters === undefined) {
        window.fbq('trackSingle', pixelId, eventName);

        return;
    }
    window.fbq('trackSingle', pixelId, eventName, parameters);
}

function buildAnalyticsTrackerName(trackingId) {
    if (!analyticsTrackerNames.has(trackingId)) {
        analyticsTrackerNames.set(trackingId, `organizer_${analyticsTrackerNames.size}`);
    }

    return analyticsTrackerNames.get(trackingId);
}

function placeGoogleAnalytics(trackingId) {
    if (!claimPlacement(buildOrganizerTagKey(VENDOR_GOOGLE_ANALYTICS, trackingId))) {
        return;
    }

    const ga = ensureUniversalAnalyticsStub();
    ensureGtag();

    ga('create', trackingId, 'auto', buildAnalyticsTrackerName(trackingId));
    appendAsyncScript(`${GTM_LOADER_URL}${encodeURIComponent(trackingId)}`);
}

function sendGoogleAnalyticsPageView(trackingId) {
    if (typeof window.ga === 'function') {
        window.ga(`${buildAnalyticsTrackerName(trackingId)}.send`, 'pageview');
    }
    ensureGtag()('config', trackingId);
}

function placeGoogleTagManager(containerId) {
    if (!claimPlacement(buildOrganizerTagKey(VENDOR_GOOGLE_TAG_MANAGER, containerId))) {
        return;
    }

    const dataLayer = ensureDataLayer();

    dataLayer.push({ 'gtm.start': Number(new Date()), event: 'gtm.js' });
    appendAsyncScript(`${GTM_LOADER_URL}${encodeURIComponent(containerId)}`);
    dataLayer.push({ 'gtm.whitelist': GOOGLE_TAG_MANAGER_WHITELIST });
}

function placeGoogleAdwords(conversionTarget) {
    if (!claimPlacement(buildOrganizerTagKey(VENDOR_GOOGLE_ADWORDS, conversionTarget))) {
        return;
    }

    const gtag = ensureGtag();

    appendAsyncScript(`${GTAG_LOADER_URL}${encodeURIComponent(conversionTarget)}`);
    gtag('config', conversionTarget);
}

function trackGoogleAdwordsConversion(sendTo) {
    // eslint-disable-next-line babel/camelcase
    ensureGtag()('event', 'conversion', { send_to: sendTo });
}

export {
    placeFacebookPixel,
    trackFacebookPixelEvent,
    placeGoogleAnalytics,
    sendGoogleAnalyticsPageView,
    placeGoogleTagManager,
    placeGoogleAdwords,
    trackGoogleAdwordsConversion,
    getServerPlacedTagKeys,
    getServerPlacedEventKeys,
    getServerDeclaredEventKeySpellings,
};
